import { firstValueFrom } from 'rxjs';
import { DatePipe } from "@angular/common";
import { Injectable } from "@angular/core";
import {
    DaysListDto,
    GetAllProjectTimeSheetDto,
    GetAllTaskDto,
    IncludeTimesheetDto,
    ProjectBillingType,
    ProjectTimesheetFrequencyEnum, TimesheetAndApprovalStatusEnum,
    TimeSheetDto, TimesheetServiceProxy, EmployeeWiseSummaryDto
} from "@shared/service-proxies/service-proxies";
import { DateTime } from "luxon";
import moment from "moment";
import { ExportTask } from "../models/export-task.model";
import { ExportTimesheet } from "../models/export-timesheet";
import * as _ from "lodash";
import * as xlsx from "xlsx-js-style";
import { map } from "rxjs/operators";
import { ToastrService } from "ngx-toastr";
import { ToastrType } from "@app/shared/common/models/toastrTypeEnum";
import { MonitoringService } from "@app/shared/common/telemetry-implementation/MonitoringService ";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { saveAs } from "file-saver";

@Injectable()
export class TimesheetUtilityService {
    dateTime = new Date();
    msg: string;
    position: any;
    projectTypes: any;
    startDate: Date;
    endDate: Date;
    constructor(
        private _monitorService: MonitoringService,
        private datePipe: DatePipe,
        private _tsProxy: TimesheetServiceProxy,
        private toastrService: ToastrService
    ) { }

    //Function for week range
    getWeekStringArray(startDate: Date, format: string): string[] {
        this._monitorService.logTrace("UI-getWeekStringArray", { "getWeekStringArray ": "getWeekStringArray:Action called " + this + this.dateTime });
        if (moment(new Date(startDate.toString())).day() === 0) {
            startDate = moment(new Date(startDate.toString())).subtract(1, "day").toDate();
        }
        const startOfWeek = moment(new Date(startDate.toString())).startOf("week").add(1, "day");
        const weekStringArray: moment.Moment[] = [startOfWeek];
        for (let i = 1; i < 7; i++) {
            weekStringArray.push(startOfWeek.clone().add(i, "day"));
        }
        return weekStringArray.map((r) => r.format(format));
    }

    getWeekStringArrayForTimesheetProject(startDate: Date, format: string): any[] {
        this._monitorService.logTrace("UI-getWeekStringArray", { "getWeekStringArray ": "getWeekStringArray:Action called " + this + this.dateTime });
        if (moment(new Date(startDate.toString())).day() === 0) {
            startDate = moment(new Date(startDate.toString())).subtract(1, "day").toDate();
        }
        const startOfWeek = moment(new Date(startDate.toString())).startOf("week").add(1, "day");
        const weekStringArray: moment.Moment[] = [startOfWeek];
        for (let i = 1; i < 7; i++) {
            weekStringArray.push(startOfWeek.clone().add(i, "day"));
        }
        return weekStringArray.map((r) => { return { date: r, toDate: r.toDate(), dateStr: r.format('MMM DD'), dayShort: r.format('ddd'), key: r.format('dddd').toLowerCase() } });

    }

    //Function for month range
    getMonthStringArray(dateInMonth: Date, format: string): string[] {
        dateInMonth = this.setZeroTimeForLocalDate(dateInMonth);
        let monthStartDate = moment(new Date(dateInMonth.toString())).startOf("month");
        let currentMonth = monthStartDate.month();
        const monthStringArray: moment.Moment[] = [];
        while (monthStartDate.month() === currentMonth) {
            monthStringArray.push(monthStartDate.clone());
            monthStartDate = monthStartDate.add(1, "day");
        }
        return monthStringArray.map((r) => r.format(format));
    }

    getMonthStringArrayForTimesheetProjectView(dateInMonth: Date, format: string): any[] {
        dateInMonth = this.setZeroTimeForLocalDate(dateInMonth);
        let monthStartDate = moment(new Date(dateInMonth.toString())).startOf("month");
        let currentMonth = monthStartDate.month();
        const monthStringArray: moment.Moment[] = [];
        while (monthStartDate.month() === currentMonth) {
            monthStringArray.push(monthStartDate.clone());
            monthStartDate = monthStartDate.add(1, "day");
        }
        return monthStringArray.map((r) => { return { date: r, toDate: r.toDate(), dateStr: r.format('MMM DD'), dayShort: r.format('ddd'), key: r.format('dddd').toLowerCase() } });

    }

    //Function for bimonthly view range
    getBimonthlyWeekStringArray(startDate: Date, format: string): any[] {
        this._monitorService.logTrace("UI-getWeekStringArray", { "getWeekStringArray ": "getWeekStringArray:Action called " + this + this.dateTime });
        if (moment(new Date(startDate.toString())).day() === 0) {
            startDate = moment(new Date(startDate.toString())).subtract(1, "day").toDate();
        }
        const startOfWeek = moment(new Date(startDate.toString())).startOf("week").add(1, "day");
        const weekStringArray: moment.Moment[] = [startOfWeek];
        for (let i = 1; i < 14; i++) {
            weekStringArray.push(startOfWeek.clone().add(i, "day"));
        }
        return weekStringArray.map((r) => r.format(format));
    }

    getBimonthlyWeekStringArrayTimesheetProjectView(startDate: Date, format: string): any[] {
        this._monitorService.logTrace("UI-getWeekStringArray", { "getWeekStringArray ": "getWeekStringArray:Action called " + this + this.dateTime });
        if (moment(new Date(startDate.toString())).day() === 0) {
            startDate = moment(new Date(startDate.toString())).subtract(1, "day").toDate();
        }
        const startOfWeek = moment(new Date(startDate.toString())).startOf("week").add(1, "day");
        const weekStringArray: moment.Moment[] = [startOfWeek];
        for (let i = 1; i < 14; i++) {
            weekStringArray.push(startOfWeek.clone().add(i, "day"));
        }
        return weekStringArray.map((r) => { return { date: r, toDate: r.toDate(), dateStr: r.format('MMM DD'), dayShort: r.format('ddd'), key: r.format('dddd').toLowerCase() } });

    }

    getRangeOfDatesStringObjectArray(startDate: Date, endDate: Date, format: string): any[] {
        startDate = this.setZeroTimeForLocalDate(startDate);
        let monthStartDate = moment(new Date(startDate.toString()));
        let monthEndDate = moment(new Date(endDate.toString()));

        const monthStringArray: moment.Moment[] = [];
        while (monthStartDate.isSameOrBefore(monthEndDate)) {
            monthStringArray.push(monthStartDate.clone());
            monthStartDate = monthStartDate.add(1, "day");
        }
        return monthStringArray.map((r) => { return { date: r, toDate: r.toDate(), dateStr: r.format('MMM DD'), dayShort: r.format('ddd'), key: r.format('dddd').toLowerCase() } });
    }

    getBiMonthlyStringArray(startDate: Date, endDate: Date,format: string):string[] {
    
        const start = moment(startDate).startOf('day');
        const end = moment(endDate).startOf('day');

        // create an array to hold the dates in the range
        const dates = [];

        const today = moment();
        const startOfWeek = moment().startOf('week');
        const endOfWeek = moment().endOf('week');
        const startOfLastWeek = moment().subtract(1, 'weeks').startOf('week');
        const endOfLastWeek = moment().subtract(1, 'weeks').endOf('week');
        const startOfNextWeek = moment().add(1, 'weeks').startOf('week');
        const endOfNextWeek = moment().add(1, 'weeks').endOf('week');

        if (today.isBetween(startOfWeek, endOfWeek)) {
            this.startDate = startOfLastWeek.toDate();
            this.endDate = endOfLastWeek.toDate();
        } else if (today.isBefore(startOfWeek)) {
            this.startDate = startOfNextWeek.toDate();
            this.endDate = endOfNextWeek.toDate();
        } else {
            this.startDate = startOfWeek.toDate();
            this.endDate = endOfWeek.toDate();
        }

        // loop through each day in the range and add it to the array
        let current = start;
        while (current.isSameOrBefore(end)) {
            dates.push(current.format(format));
            current.add(1, 'day');
        }

        return dates;

    }

    loadEmptyHashMapForMonth(currentDate: Date): { [dateInMonth: number]: { task: GetAllTaskDto; minutes: number }[] } {
        let totalDaysInMonth = moment(new Date(this.setZeroTimeForLocalDate(currentDate).toString())).daysInMonth();
        return new Array(totalDaysInMonth)
            .fill(0)
            .map((r, i) => i + 1)
            .reduce((a, v) => ({ ...a, [v]: [] }), {});
    }
    loadEmptyHashMapForMonths(currentDate: Date): { [dateInMonth: number]: { task: GetAllProjectTimeSheetDto; minutes: number }[] } {
        let totalDaysInMonth = moment(new Date(this.setZeroTimeForLocalDate(currentDate).toString())).daysInMonth();
        return new Array(totalDaysInMonth)
            .fill(0)
            .map((r, i) => i + 1)
            .reduce((a, v) => ({ ...a, [v]: [] }), {});
    }

    getWeekRangeFromDate(date: Date): Date[] {
        if (moment(new Date(date.toString())).day() === 0) {
            date = moment(new Date(date.toString())).subtract(1, "day").toDate();
        }
        return [moment(new Date(date.toString())).startOf("week"), moment(new Date(date.toString())).endOf("week")]
            .map((r) => r.add(1, "day"))
            .map((r) => r.toDate())
            .map((r) => this.setZeroTimeForLocalDate(r));
    }

    getMonthRangeFromDate(date: Date): Date[] {
        return [moment(new Date(date.toString())).startOf("month"), moment(new Date(date.toString())).endOf("month")].map((r) => r.toDate());
    }

    modifyDayTimeStamps(daysListDtos: DaysListDto[]): void {
        daysListDtos.forEach((r) => {
            Object.keys(r).forEach((x) => {
                if (r[x] && typeof r[x] === "string") {
                    r[x] = this.trimTimestamp(r[x]);
                }
            });
        });
    }

    trimTimestamp(timestamp: string): string {
        if (!timestamp) return "00:00";
        if (!timestamp.includes(':')) return timestamp.split(":")[0] + ":" + "00";
        return timestamp.split(":")[0] + ":" + timestamp.split(":")[1];
    }

    timestampToMinutes(timestamp: string): number {
        if (!timestamp) return 0;
        let hrs = timestamp.split(":")[0] ? timestamp.split(":")[0]: "00"
        let min = timestamp.split(":")[1] ? timestamp.split(":")[1]: "00"
        return parseInt(hrs) * 60 + parseInt(min);
    }

    minutesToTimestamp(minutes: number): string {
        let hours = Math.floor(minutes / 60);
        let min = Math.floor(minutes % 60);
        if (isNaN(hours) || isNaN(min)) return "--:--";
        return `${hours < 10 ? "0" + hours : hours}:${min < 10 ? "0" + min : min}`;
    }

    addSecondsToTimespan(timespan: string): string {
        if (timespan.split(":").length > 2) return timespan;
        return timespan + ":00";
    }

    getTotalHoursForWeek(daysListDto: DaysListDto): string {
        let sum = 0;
        Object.keys(daysListDto).forEach((x) => {
            if (daysListDto[x] && typeof daysListDto[x] === "string") {
                sum += this.timestampToMinutes(this.trimTimestamp(daysListDto[x]));
            }
        });
        return this.minutesToTimestamp(sum);
    }

    timerToHrsMin(timer: string) {
        let arr = timer.split(":");
        return arr[0] + ":" + arr[1] + " h";
    }
    getTotalTimeForADay(projectList: GetAllProjectTimeSheetDto[] | GetAllTaskDto[], key: string): string {
        let sum = 0;
        if (projectList) {
            projectList.forEach((r) => {
                for (const property in r.datesListDto) {
                    if (key === property && r.includeInTimesheet) {
                        sum += this.timestampToMinutes(r.datesListDto[property]);
                    }

                }
            });
        }
        let timerHrsMins = this.minutesToTimestamp(sum);
        return timerHrsMins;
    }
    getTotalTimeForADayInHrMin(projectList: GetAllProjectTimeSheetDto[] | GetAllTaskDto[], key: string): string {
        let timer = this.getTotalTimeForADay(projectList, key);
        return this.timerToHrsMin(timer);
    }
    getTotalTimeForADayProjectTasks(projectList: GetAllProjectTimeSheetDto[] | GetAllTaskDto[], key: string): string {
        let sum = 0;
        if (projectList) {
            projectList.forEach((r) => {
                sum += this.timestampToMinutes(r.daysListDtos[0][key]);
            });
        }
        let timerHrsMins = this.minutesToTimestamp(sum);
        return this.timerToHrsMin(timerHrsMins);

    }
    getBilledTimeForADay(projectList: GetAllProjectTimeSheetDto[] | GetAllTaskDto[], key: string): string {
        let sum = 0;
        if (projectList) {
            projectList.forEach((r) => {
                if (r.includeInTimesheet && r.billabilityStatus == 1) sum += this.timestampToMinutes(r.daysListDtos[0][key]);
            });
        }
        return this.minutesToTimestamp(sum);
    }

    getTotalTimeForAWeek(projectList: GetAllProjectTimeSheetDto[] | GetAllTaskDto[]): string {
        let sum = 0;
        if (projectList) {

            projectList.forEach((r) => {

                for (const property in r.datesListDto) {
                    if (r.includeInTimesheet) {
                        sum += this.timestampToMinutes(r.datesListDto[property]);
                    }
                }
            });

        }
        let timerHrsMins = this.minutesToTimestamp(sum);
        return this.timerToHrsMin(timerHrsMins);
    }
    getTotalTimeForAWeekProjectTasks(projectList: GetAllProjectTimeSheetDto[] | GetAllTaskDto[]): string {
        let sum = 0;
        if (projectList) {
            projectList.forEach((r) => {
                sum += this.timestampToMinutes(this.getTotalHoursForWeek(r.daysListDtos[0]));
            });
        }
        let timerHrsMins = this.minutesToTimestamp(sum);
        return this.timerToHrsMin(timerHrsMins);
    }
    getTotalBilledTimeForAWeek(projectList: GetAllProjectTimeSheetDto[] | GetAllTaskDto[]): string {
        let sum = 0;
        if (projectList) {
            projectList.forEach((r) => {
                if (r.includeInTimesheet && r.billabilityStatus == 1) sum += this.timestampToMinutes(this.getTotalHoursForWeek(r.daysListDtos[0]));
            });
        }
        return this.minutesToTimestamp(sum);
    }

    isDateValid(date: any) {
        if (!date) return false;
        if (typeof date === "string") return false;
        if (date.year === 1 || date.month === 1) return false;
        return true;
    }

    timespanToHrsMin(timespan: string): string {
        if (!timespan) return "00:00 min";
        return timespan.split(":")[0] + ":" + timespan.split(":")[1];
    }

    timespanToHrsMinSec(timespan: string): string {
        if (!timespan) return "00:00";
        timespan = this.convertDecimalTimespanIntoNormalTimeSpan(timespan);
        return timespan.split(":")[0] + ":" + timespan.split(":")[1] + ":" + timespan.split(":")[2].split(".")[0];
    }
    getTimesheetFrequency(frequency: ProjectTimesheetFrequencyEnum) {
        if (!frequency) return "Not available";
        return ProjectTimesheetFrequencyEnum[frequency].replace(/([A-Z])/g, " $1").trim();
    }

    getTimesheetStatus(status: TimesheetAndApprovalStatusEnum) {
        if (!status) return "Not available";
        if (status === TimesheetAndApprovalStatusEnum.OverdueForApproval) {
            return TimesheetAndApprovalStatusEnum[status]
                .replace(/([A-Z])/g, " $1")
                .trim()
                .split(" ")[0];
        }
        return TimesheetAndApprovalStatusEnum[status].replace(/([A-Z])/g, " $1").trim();
    }

    addTimespans(timespans: string[]): string {
        if (!timespans) return "00:00";
        return this.minutesToTimestamp(
            timespans.reduce((a, v) => {
                a += this.timestampToMinutes(v);
                return a;
            }, 0)
        );
    }
    getProjectBillingType(type: ProjectBillingType) {
        if (!type) return "Not available";
        if (ProjectBillingType[type] == 'NonBillable') {
            this.position = 3;
            let dash = '-';
            this.projectTypes = [ProjectBillingType[type].slice(0, this.position), dash, ProjectBillingType[type].slice(this.position)].join('');
        }
        if (ProjectBillingType[type] == 'Billable') {
            this.projectTypes = ProjectBillingType[type];
        }
        return this.projectTypes.replace(/([A-Z])/g, "$1").trim();
    }

    // get local js date from a UTC datetime object
    getLocalJsDate(date: DateTime): Date {
        let jsDate = new Date(
            `${date?.year}-${date?.month < 10 ? "0" + date?.month : date?.month}-${date?.day < 10 ? "0" + date?.day : date?.day}T${date?.hour < 10 ? "0" + date?.hour : date?.hour
            }:${date?.minute < 10 ? "0" + date?.minute : date?.minute}:${date?.second < 10 ? "0" + date?.second : date?.second}`
        );
        return new Date(jsDate.getTime() - jsDate.getTimezoneOffset() * 60 * 1000);
    }

    getTimesheetRangeFromFrequency(dateInCurrentWeek: Date, submissionFrequency: ProjectTimesheetFrequencyEnum, projectStartDate?: Date): Date[] {
        if (submissionFrequency === ProjectTimesheetFrequencyEnum.Daily) {
            return [this.setZeroTimeForLocalDate(dateInCurrentWeek), this.setZeroTimeForLocalDate(dateInCurrentWeek)];
        } else if (submissionFrequency === ProjectTimesheetFrequencyEnum.Weekly) {
            return this.getWeekRangeFromDate(dateInCurrentWeek).map((r) => this.setZeroTimeForLocalDate(r));
        } else if (submissionFrequency === ProjectTimesheetFrequencyEnum.BiMonthly) {
            return this.getBimonthlyRangeFromProjectStartDate(projectStartDate, dateInCurrentWeek).map((r) => this.setZeroTimeForLocalDate(r));
        } else if (submissionFrequency === ProjectTimesheetFrequencyEnum.Monthly) {
            return this.getMonthRangeFromDate(dateInCurrentWeek).map((r) => this.setZeroTimeForLocalDate(r));
        } else {
            return undefined;
        }
    }

    getBimonthlyRangeFromProjectStartDate(projectStartDate: Date, dateInCurrentWeek: Date): Date[] {

        let projectStartDateMoment = moment(new Date(projectStartDate.toString())).clone();
        projectStartDate.setHours(0, 0, 0, 0);
        dateInCurrentWeek.setHours(0, 0, 0, 0);
        projectStartDate = this.setZeroTimeForLocalDate(projectStartDate);
        dateInCurrentWeek = this.setZeroTimeForLocalDate(dateInCurrentWeek);
        let currentRange: moment.Moment[] = [];

        if (moment(new Date(projectStartDate.toString())).day() === 0) {
            currentRange[0] = moment(new Date(projectStartDate.toString())).subtract(1, "day").clone().startOf('week').clone().add(1, 'day');
        } else {
            currentRange[0] = moment(new Date(projectStartDate.toString())).startOf("week").clone().add(1, "day");
        }
        currentRange[1] = currentRange[0].clone().add(13, 'days')
        if (currentRange[0].toDate().getTime() <= dateInCurrentWeek.getTime()) {
            while (!moment(new Date(dateInCurrentWeek.toString())).isBetween(currentRange[0], currentRange[1], undefined, "[]")) {
                currentRange[0] = currentRange[0].clone().add(14, "days");
                currentRange[1] = currentRange[0].clone().add(13, "days");
            }
        } else {
            while (!moment(new Date(dateInCurrentWeek.toString())).isBetween(currentRange[0], currentRange[1], undefined, "[]")) {
                currentRange[0] = currentRange[0].clone().subtract(14, "days");
                currentRange[1] = currentRange[0].clone().add(13, "days");
            }
        }
        if (moment(new Date(projectStartDate.toString())).isBetween(currentRange[0], currentRange[1], undefined, "[]")) {
            currentRange[0] = projectStartDateMoment;
        }
        return currentRange.map((r) => r.toDate());
    }

    setZeroTimeForLocalDate(date: Date): Date {
        date.setHours(0, 0, 0, 0);
        return new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000);
    }

    getTimesheetSubmissionRangeSubMessage(timesheetSubmissionRange: Date[]) {
        if (timesheetSubmissionRange) {
            if (timesheetSubmissionRange[0].getTime() === timesheetSubmissionRange[1].getTime()) {
                return `Your timesheet for ${this.datePipe.transform(timesheetSubmissionRange[0], "MMM d")} will be submitted`;
            }
            return `Your timesheet for the period ${this.datePipe.transform(timesheetSubmissionRange[0], "MMM d")} to ${this.datePipe.transform(
                timesheetSubmissionRange[1],
                "MMM d"
            )} will be submitted`;
        }
    }

    isTaskAPartOfPendingTimesheet(task: GetAllTaskDto, date: Date): boolean {
        if (task.fromDate?.length && task.toDate?.length) {
            const fromAndToDateArray = task.fromDate.map((r, i) => {
                return { fromDate: r, toDate: task.toDate[i] };
            })
            return _.some(fromAndToDateArray, x =>
                this.isDateInRange(date, [x.fromDate, x.toDate].map(t => this.getLocalJsDate(t)))
            )
        }
        return false;
    }

    isThisInputDisabled(
        project: GetAllProjectTimeSheetDto,
        timesheet: TimeSheetDto,
        isMyTimesheet: boolean,
        task: GetAllTaskDto,
        weekRange: Date[],
        index: number,
        timesheetTasks: GetAllTaskDto[]
    ): boolean {

        let cellDate = this.getDateOfTheWeekfromWeekRange(weekRange, index);
        // if the view is for project tasks
        if (project) {
            // block input if the task is in task.taskIdList
            if (this.isTaskAPartOfPendingTimesheet(task, cellDate)) return true;
            // block input if cell date is out of project duration
            if (this.isDateOutOfEmployeeDurationWithinProject(cellDate, project)) return true;

            //if timesheet is submitted on current task date
            let rec = timesheetTasks.filter(tsTask => (this.isTaskAPartOfPendingTimesheet(tsTask, cellDate)))?.length;
            if (rec) return true;

        }

        // if the view is for timesheet tasks
        if (timesheet) {
            //  block editing if status of any timesheet is rejected
            if(timesheet?.dateWiseTimesheetIds?.length && isMyTimesheet) {
               let timesheetStatus = timesheet.dateWiseTimesheetIds.find(x => x.timesheetSubmitDate.hasSame(DateTime.fromJSDate(cellDate), 'day'))?.approvalLevelTimesheetStatus 
               return !(timesheetStatus == TimesheetAndApprovalStatusEnum.Rejected)? true : false

            }
            
            // block editing if status of timesheet is rejected and cell date is out of project duration
            if (timesheet && timesheet.employeeProjectStartDate && timesheet.employeeProjectEndDate) {
                return (
                    cellDate.getTime() < this.setZeroTimeForLocalDate(this.getLocalJsDate(timesheet.employeeProjectStartDate)).getTime() ||
                    cellDate.getTime() > this.setZeroTimeForLocalDate(this.getLocalJsDate(timesheet.employeeProjectEndDate)).getTime()
                );
            }
            // block editing if timesheet status is anything other than rejected
            if (timesheet.timesheetStatus !== TimesheetAndApprovalStatusEnum.Rejected) return true;

            // if status of timesheet is rejected and not my timesheet block editing
            if (timesheet.timesheetStatus === TimesheetAndApprovalStatusEnum.Rejected && !isMyTimesheet) return true;

            // if status of timesheet is rejected and it is my timesheet
            if (timesheet.timesheetStatus === TimesheetAndApprovalStatusEnum.Rejected && isMyTimesheet) {
                // --- 1) block editing if cell date is not within timesheet.fromDate and timesheet.toDate
                let projectStartDate = this.getLocalJsDate(timesheet.projectCreationDate);
                let projectEndDate = this.getLocalJsDate(timesheet.projectEndDate);
                let timesheetFromDate = this.getLocalJsDate(timesheet.fromDate);
                let timesheetToDate = this.getLocalJsDate(timesheet.toDate);

                if (!this.isDateInRange(cellDate, [projectStartDate, projectEndDate])) return true;
                if (!this.isDateInRange(cellDate, [timesheetFromDate, timesheetToDate])) return true;
            }
        }
        return false;
    }

    isDateOutOfEmployeeDurationWithinProject(date: Date, project: GetAllProjectTimeSheetDto, timesheet?: TimeSheetDto) {
        if (project && project.employeeProjectStartDate && project.employeeProjectEndDate) {
            return (
                date.getTime() < this.setZeroTimeForLocalDate(this.getLocalJsDate(project.employeeProjectStartDate)).getTime() ||
                date.getTime() > this.setZeroTimeForLocalDate(this.getLocalJsDate(project.employeeProjectEndDate)).getTime()
            );
        } else if (timesheet && timesheet.projectEndDate) {
            return date.getTime() > this.setZeroTimeForLocalDate(this.getLocalJsDate(timesheet.projectEndDate)).getTime();
        }
        return false;
    }

    isCellBeforeTaskCreationDate(task: GetAllTaskDto, date: Date): boolean {
        return date.getTime() < this.setZeroTimeForLocalDate(this.getLocalJsDate(task.creationDay)).getTime();
    }

    isCellAfterTimesheetToDate(date: Date, toDate: Date): boolean {
        return date.getTime() > this.setZeroTimeForLocalDate(toDate).getTime();
    }

    showTopToastr(type: ToastrType, message: string, title?: string,) {
        if (type === ToastrType.Info) this.toastrService.info(message, title)
        if (type === ToastrType.Success) this.toastrService.success(message, title)
        if (type === ToastrType.Warning) this.toastrService.warning(message, title)
        if (type === ToastrType.Error) this.toastrService.error(message, title)
    }

    getTimesheetDateRange(timesheet: TimeSheetDto) {
        if (
            timesheet.fromDate.day === timesheet.toDate.day &&
            timesheet.fromDate.month === timesheet.toDate.month &&
            timesheet.fromDate.year === timesheet.toDate.year
        ) {
            return `Timesheet ${moment(new Date(this.getLocalJsDate(timesheet.fromDate).toString())).format("MMM DD")}`;
        } else {
            return `Timesheet ${moment(new Date(this.getLocalJsDate(timesheet.fromDate).toString())).format("MMM DD")} - ${moment(
                new Date(this.getLocalJsDate(timesheet.toDate).toString())
            ).format("MMM DD")}`;
        }
    }

    getTimesheetDateRangeOnlyDates(timesheet: TimeSheetDto) {
        if (
            timesheet.fromDate.day === timesheet.toDate.day &&
            timesheet.fromDate.month === timesheet.toDate.month &&
            timesheet.fromDate.year === timesheet.toDate.year
        ) {
            return `${moment(new Date(this.getLocalJsDate(timesheet.fromDate).toString())).format("MMM DD")}`;
        } else {
            return `${moment(new Date(this.getLocalJsDate(timesheet.fromDate).toString())).format("MMM DD")} - ${moment(
                new Date(this.getLocalJsDate(timesheet.toDate).toString())
            ).format("MMM DD")}`;
        }
    }

    groupComments(comments: any[]): any[][] {
        let arr: any[][] = [];
        for (let i = 0; i < comments.length; i++) {
            if (i === 0 || comments[i].userName !== comments[i - 1].userName) {
                arr.push([comments[i]]);
            } else if (comments[i].userName === comments[i - 1].userName) {
                _.last(arr).push(comments[i]);
            }
        }
        return arr;
    }

    getDateOfTheWeekfromWeekRange(weekRange: Date[], index: number): Date {
        return this.setZeroTimeForLocalDate(moment(new Date(weekRange[0].toString())).add(index, "days").toDate());
    }

    getTimeDifferenceInTimespan(startDate: Date, endDate: Date): string {
        let duration: moment.Duration = moment.duration(moment(new Date(endDate.toString())).diff(moment(startDate.toString())));
        return duration.hours + ":" + duration.minutes;
    }

    isDateSame(date1: Date, date2: Date): boolean {
        return this.setZeroTimeForLocalDate(date1).getTime() === this.setZeroTimeForLocalDate(date2).getTime();
    }

    isDateInRange(date: Date, range: Date[]) {
        return (
            moment(new Date(date.toString())).isBetween(moment(new Date(range[0].toString())), moment(new Date(range[1].toString())), undefined, "[]") ||
            moment(new Date(date.toString())).isSame(moment(new Date(range[0].toString()))) ||
            moment(new Date(date.toString())).isSame(moment(new Date(range[1].toString())))
        );
    }

    stopEventPropogation(event: Event) {
        event.stopPropagation();
    }

    filterTasksIncludedInTimesheet(timesheetTasks: GetAllTaskDto[]): IncludeTimesheetDto[] {
        return timesheetTasks
            .map((r) => {
                let x = new IncludeTimesheetDto();
                x.taskId = r.userTaskId;
                x.includeTimesheet = r.includeInTimesheet;
                return x;
            })
            .filter((r) => r.includeTimesheet);
    }

    isInputTimeValid(timespan: string) {
        if (timespan.includes('.')) {
            return /^(0?[0-9]|1?[0-9]|2?[0-3]|24)\.?[0-9]?[0-9]?$/.test(timespan);
        }
        // Allow 24 / 24:00 while typing (old pattern stopped at 23 and flagged "24" as invalid).
        return /^(0?[0-9]|1?[0-9]|2?[0-3]|24):?[0-5]?[0-9]?$/.test(timespan);
    }

    getWeekDateArrayFromWeekRange(weekRange: Date[]): Date[] {
        let dateArray: Date[] = [];
        for (let i = 0; i < 7; i++) {
            dateArray.push(moment(new Date(weekRange[0].toString())).clone().add(i, "days").toDate());
        }
        return dateArray;
    }

    calculateSubmittedHoursForTasks(tasks: GetAllTaskDto[], dates: Date[]): string {
        let totalMinutes = 0;
        tasks.forEach((t, i) => {
            dates.forEach((d, j) => {
                let day = moment(new Date(d.toString())).format("dddd").toLowerCase();
                totalMinutes += this.timestampToMinutes(t.daysListDtos[0][day]);
            });
        });
        return this.trimTimestamp(this.minutesToTimestamp(totalMinutes));
    }

    mapTaskToExportTask(task: GetAllTaskDto | any): ExportTask {
        let taskName = task.taskName;
        let billability;
        if (!task.billabilityStatus) {
            billability = "-";
        } else {
            billability = task.billabilityStatus === 1 ? "Yes" : "No";
        }
        let datesArray = [];
        task.datesListDto = this.sortObjectBykeys(task.datesListDto);
        for (const property in task.datesListDto) {
            datesArray.push(this.timestampToMinutes(task.datesListDto[property]));
        }
        let totalTime = this.getTotalHoursForWeek(task.datesListDto);
        let showInTimesheet = task.includeInTimesheet ? "Yes" : "No";
        return {
            task: taskName,
            billability: billability,
            ...task.datesListDto,
            // mondayDate,
            // tuesdayDate,
            // wednesdayDate,
            // thursdayDate,
            // fridayDate,
            // saturdayDate,
            // sundayDate,
            totalTime: totalTime,
            showInTimesheet: showInTimesheet
    }
    }

    mapTimesheetToExportTimesheet(timesheet: TimeSheetDto): ExportTimesheet {
        let memberName = timesheet.userName;
        let projectName = timesheet.projectName;
        let timesheetDate =
            `${moment(new Date(this.getLocalJsDate(timesheet.fromDate).toString())).format("MM/DD/YYYY")}` +

            (!this.isDateSame(timesheet.fromDate.toJSDate(), timesheet.toDate.toJSDate())
                ? `- ${moment(new Date(this.getLocalJsDate(timesheet.toDate).toString())).format("MM/DD/YYYY")}`

                : "");
        let submittedOn = moment(new Date(this.getLocalJsDate(timesheet.submitedDate).toString())).format("MM/DD/YYYY");
        let submittedHours = this.getHoursAndMinStringWithColon(timesheet.submitedHours);
        let requiredHours = this.getHoursAndMinStringWithColon(timesheet.requiredHours);
        let billedHours = this.getHoursAndMinStringWithColon(timesheet.billedHours);
        let status = this.getTimesheetStatus(timesheet.timesheetStatus);
        let leaves = timesheet.leave.toString();

        return new ExportTimesheet(
            submittedOn,
            memberName,
            projectName,
            timesheetDate,
            requiredHours,
            // timesheetFrequency,
            submittedHours,
            billedHours,
            status,
            leaves
        );
    }
    getHoursAndMin(timespan: string) {
        if (!timespan) return '00h 00m';
        let hours = parseInt(timespan.split(":")[0]);
        let min = parseInt(timespan.split(":")[1]);
        return `${hours < 10 ? "0" + hours : hours}:${min < 10 ? "0" + min : min} h`;
    }

    convertDecimalTimespanIntoNormalTimeSpan(timespan: string) {
        if (timespan.split(":")[0].includes(".")) {
            let min = parseInt(timespan.split(":")[1]);
            let sec = timespan.split(":")[2] ? parseInt(timespan.split(":")[2]) : null;
            let days = parseInt(timespan.split(":")[0].split(".")[0]);
            let newHours = parseInt(timespan.split(":")[0].split(".")[1]);
            let hours;
            if (days < 0) {
                hours = days * 24 - newHours;
            } else {
                hours = days * 24 + newHours;
            }
            if (sec) return `${hours < 10 && hours >= 0 ? "0" + hours : hours}:${min < 10 ? "0" + min : min}:${sec < 10 ? "0" + sec : sec}`;
            if (!sec) return `${hours < 10 && hours >= 0 ? "0" + hours : hours}:${min < 10 ? "0" + min : min}:00`;
        } else {
            return timespan;
        }
    }

    getTimesheetFromToDateString(fromDate: DateTime, toDate: DateTime) {
        if (
            this.setZeroTimeForLocalDate(this.getLocalJsDate(fromDate)).getTime() ===
            this.setZeroTimeForLocalDate(this.getLocalJsDate(toDate)).getTime()
        ) {
            return moment(new Date(this.setZeroTimeForLocalDate(this.getLocalJsDate(fromDate)))).format("DD MMM YYYY");

        }

        return (
            moment(new Date(this.setZeroTimeForLocalDate(this.getLocalJsDate(fromDate)))).format("DD MMM YYYY") +
            " - " +
            moment(new Date(this.setZeroTimeForLocalDate(this.getLocalJsDate(toDate)))).format("DD MMM YYYY")
        );
    }

    exportPdfTasks(
        data: any[],
        columns: { title: string; dataKey: string }[],
        name: string,
        pdfTitle: string,
        weekDays: { dayShort: string; dateStr: string; key: string; total: number }[],
        project: GetAllProjectTimeSheetDto
    ) {
        let totalForWeek = data.reduce((a, v) => {
            a += this.timestampToMinutes(v.totalTime);
            return a;
        }, 0);
        let obj = {};
        weekDays.map(x => { return obj[x.dateStr] = ''})
        let lastRow = {task:"-", billability:"-", ...obj, totalTime: this.minutesToTimestamp(totalForWeek), showInTimesheet:"-"};
        data.push(lastRow);
        let exportColumns = columns;
        const doc = new jsPDF("l", "pt",[weekDays.length > 25 ? 1600 : 850, 600]);
        doc.text(pdfTitle, 40, 40);
        doc.setFontSize(12);
        doc.text(`Project name: ${_.capitalize(project.projectName)}`, 40, 60);
        doc.text(`Period: ${weekDays[0].dateStr} - ${_.last(weekDays).dateStr}`, 40, 100);
        doc.autoTable(exportColumns, data, {
            startY: 120,
        });
        doc.save(`${name} ${weekDays[0].dateStr} - ${_.last(weekDays).dateStr}.pdf`);
    }

    exportExcelTasks(
        data: any[],
        name: string,
        weekDays: { dayShort: string; dateStr: string; key: string; total: number }[],
        project: GetAllProjectTimeSheetDto
    ) {
        let totalForWeek = data.reduce((a, v) => {
            a += this.timestampToMinutes(v.totalTime);
            return a;
        }, 0);
        let obj = {};
        weekDays.map(x => { return obj[x.dateStr] = ''})
        let lastRow = {task:"-", billability:"-", ...obj , totalTime: this.minutesToTimestamp(totalForWeek), showInTimesheet:"-"};
        data.push(lastRow);
        const workbook: xlsx.WorkBook = xlsx.utils.book_new();
        const worksheet: xlsx.WorkSheet = xlsx.utils.aoa_to_sheet([]);
        xlsx.utils.sheet_add_aoa(worksheet, [[ _.capitalize(project.projectName) + " Tasks"]], { origin: "A1" });
        xlsx.utils.sheet_add_aoa(worksheet, [["Project Name", _.capitalize(project.projectName)]], { origin: "A2" });
        xlsx.utils.sheet_add_aoa(worksheet, [["Period", `${weekDays[0].dateStr} - ${_.last(weekDays).dateStr}`]], {
            origin: "A3",
        });

        xlsx.utils.sheet_add_aoa(
            worksheet,
            [
                [
                    "",
                    "",
                    ...weekDays.map(x=> x.dateStr),
                    // weekDays[1].dateStr,
                    // weekDays[2].dateStr,
                    // weekDays[3].dateStr,
                    // weekDays[4].dateStr,
                    // weekDays[5].dateStr,
                    // weekDays[5].dateStr,
                    "",
                    "",
                ],
            ],
            {
                origin: "A6",
            }
        );
        xlsx.utils.sheet_add_aoa(
            worksheet,
            [
                [
                    "Task Name",
                    "Billability",
                    ...weekDays.map(x => x.key.charAt(0).toUpperCase() + x.key.slice(1)),
                    "Total Time",
                    "Show In Timesheet",
                ],
            ],
            {
                origin: "A7",
            }
        );
        xlsx.utils.sheet_add_json(worksheet, data, { origin: "A8", skipHeader: true });

        // styles
        worksheet["!cols"] = [
            { width: 20 },
            { width: 20 },
            ...weekDays.map(x=> {return { width: 20 }}),
            { width: 20 },
            { width: 20 },
        ];

                      // Merge and center cells A1 to F1
worksheet["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 6 } }]
        for (let i in worksheet) {
            if (typeof worksheet[i] != "object") continue;
            let cell = xlsx.utils.decode_cell(i);
            worksheet[i].s = {
                font: {
                    name: "Calibri",
                    sz: 11,
                    color: { rgb: "000000" },
                },
                border: {
                    top: {
                        style: "thin",
                        color: { rgb: "000000" },
                    },
                    bottom: {
                        style: "thin",
                        color: { rgb: "000000" },
                    },
                    left: {
                        style: "thin",
                        color: { rgb: "000000" },
                    },
                    right: {
                        style: "thin",
                        color: { rgb: "000000" },
                    },
                },
            };

            if (cell.r == 5 || cell.r == 6) {
                worksheet[i].s.font.bold = true;
                worksheet[i].s.fill = {
                    patternType: "solid",
                    fgColor: { rgb: "DDDDDD" },
                };
            }

            if (worksheet[i].v === "Total") {
                worksheet[i].s.font.bold = true;
            }
            if ((cell.r == 1 || cell.r == 2 || cell.r == 3) && cell.c == 0) {
                worksheet[i].s.font.bold = true;
                worksheet[i].s.fill = {
                    patternType: "solid",
                    fgColor: { rgb: "DDDDDD" },
                };
            }
            if (cell.r == 0) {
                worksheet[i].s.font.bold = true;
                worksheet[i].s.font.underline = true; 
                worksheet[i].s.font.sz = 12;
                worksheet[i].s.fill = {
                    patternType: "solid",
                    fgColor: { rgb: "FFFFFF" },
                };
                worksheet[i].s.alignment = {
                    vertical: "center",
                        horizontal: "center",
                        wrapText: '1',
                }
            }
        }

        xlsx.utils.book_append_sheet(workbook, worksheet, "Sheet 1");
        const excelBuffer: any = xlsx.write(workbook, {
            bookType: "xlsx",
            type: "array",
        });

        this.saveAsExcelFile(excelBuffer, name);
    }

    async exportPdfForTimesheetTasks(timesheet: TimeSheetDto, individualTimesheet:GetAllTaskDto[], dateHeaders) {
        const doc = new jsPDF("l", "pt", [dateHeaders?.length > 25 ? 1600 : 850, 600]);
        doc.text("Timesheet Report", 40, 40);
        doc.setFontSize(12);
        doc.text(`Project name: ${_.capitalize(timesheet.projectName)}`, 40, 80);
        doc.text(`Member name: ${_.capitalize(timesheet.userName)}`, 40, 100);
        doc.text(`Timesheet period: ${this.getTimesheetFromToDateString(timesheet.fromDate, timesheet.toDate)}`, 40, 120);
        let firstRowWritten = false;
        let totalForTimesheet = 0;
            let cols = [
                { dataKey: "task", title: "Task" },
                { dataKey: "billability", title: "Billability" },
                ...dateHeaders,
                { dataKey: "totalTime", title: "Total time" },
                { dataKey: "showInTimesheet", title: "Show in timesheet" },
            ];
            totalForTimesheet += individualTimesheet
                .map((r) => this.mapTaskToExportTask(r))
                .reduce((a, v) => (a += this.timestampToMinutes(v.totalTime)), 0);
            if (individualTimesheet.length) {
                if (!firstRowWritten) {
                    doc.autoTable(
                        cols,
                        individualTimesheet.map((r) => this.mapTaskToExportTask(r)),
                        {
                            startY: 160,
                        }
                    );
                    firstRowWritten = true;
                } else {
                    doc.autoTable(
                        cols,
                        individualTimesheet.map((r) => this.mapTaskToExportTask(r))
                    );
                }
            }
        doc.text(`Total time for the timesheet: ${this.minutesToTimestamp(totalForTimesheet)}`, 40, 140);
        doc.save(`${_.capitalize(timesheet.projectName)} - ${_.capitalize(timesheet.userName)} - ${this.getTimesheetDateRangeOnlyDates(timesheet)}`);
    }

    async exportExcelForTimesheetTasks(timesheet: TimeSheetDto, isMyTimesheet: boolean) {
        const workbook: xlsx.WorkBook = xlsx.utils.book_new();
        const worksheet: xlsx.WorkSheet = xlsx.utils.aoa_to_sheet([]);
        xlsx.utils.sheet_add_aoa(worksheet, [["Timesheet Report"]], { origin: "A1" });
        xlsx.utils.sheet_add_aoa(worksheet, [["Project Name", _.capitalize(timesheet.projectName)]], { origin: "A2" });
        xlsx.utils.sheet_add_aoa(worksheet, [["Member Name", _.capitalize(timesheet.userName)]], { origin: "A3" });
        xlsx.utils.sheet_add_aoa(worksheet, [["Timesheet Period", this.getTimesheetFromToDateString(timesheet.fromDate, timesheet.toDate)]], {
            origin: "A4",
        });

        let timesheetRangeArray = this.getDateArrayFromDateRange(
            this.setZeroTimeForLocalDate(this.getLocalJsDate(timesheet.fromDate)),
            this.setZeroTimeForLocalDate(this.getLocalJsDate(timesheet.toDate))
        ).map((r) => {
            return moment(new Date(r.toString())).format("MMM DD");
        });

        let headerArray = ["Task Name", "Billability", ...timesheetRangeArray, "Total Time", "Show In Timesheet"];

        xlsx.utils.sheet_add_aoa(worksheet, [headerArray], {
            origin: "A6",
        });

        let datesArray = this.getDatesArrayFromFrequency(timesheet, timesheet.timesheetFrequency);

        let dataGrid: string[][] = [];

        let d = new Date();

        for (let i = 0; i < datesArray.length; i++) {
            let dateInWeek = datesArray[i];
            let daysInWeek = this.getWeekDateArrayFromWeekRange(this.getWeekRangeFromDate(dateInWeek));
            let dateRange;
            if (
                timesheet.timesheetFrequency === ProjectTimesheetFrequencyEnum.BiMonthly ||
                timesheet.timesheetFrequency === ProjectTimesheetFrequencyEnum.Monthly
            ) {
                dateRange = this.getTimesheetRangeFromFrequency(
                    datesArray[i],
                    timesheet.timesheetFrequency,
                    timesheet.timesheetFrequency === ProjectTimesheetFrequencyEnum.BiMonthly
                        ? this.getLocalJsDate(timesheet.projectCreationDate)
                        : undefined
                );
            }
            
            let individualTimesheet = await firstValueFrom(this._tsProxy
                .getAllMyTimesheetTaskByProjectId(
                    timesheet.projectId,
                    timesheet.id,
                    isMyTimesheet ? 0 : timesheet.userId,
                    this.getWeekStringArray(datesArray[i], "DD MMMM YYYY"),
                    dateRange ? DateTime.fromJSDate(dateRange[0]) : undefined,
                    dateRange ? DateTime.fromJSDate(dateRange[1]) : undefined,
                    timesheet.timesheetFrequency,
                    d.getTimezoneOffset()
                )
                .pipe(
                    map((r) => {
                        return r.filter((x) => x.includeInTimesheet);
                    })
                ));

            for (let i = 0; i < individualTimesheet.length; i++) {
                let task = individualTimesheet[i];
                if (dataGrid.findIndex((r) => r[0]?.toString() === task.userTaskId.toString()) === -1) {
                    let arr = [];
                    arr.push(task.userTaskId.toString());
                    arr.push(task.taskName.toString());
                    arr.push(task.billabilityStatus ? (task.billabilityStatus === 1 ? 'Yes' : 'No') : "-");
                    arr.push(...new Array(timesheetRangeArray.length).fill("00:00"));
                    arr.push("00:00");
                    arr.push(task.includeInTimesheet ? "Yes" : "No");
                    dataGrid.push(arr);
                }
                for (let j = 0; j < daysInWeek.length; j++) {
                    let date = this.setZeroTimeForLocalDate(daysInWeek[j]);
                    let dateStr = moment(new Date(daysInWeek[j].toString())).format("dddd").toLowerCase();
                    let timespan = task.daysListDtos[0][dateStr];
                    let index = timesheetRangeArray
                        .map((r) => {
                            return moment(new Date(r.toString())).format("MMM DD");
                        })
                        .findIndex((r) => r === moment(new Date(date.toString())).format("MMM DD"));
                    if (index !== -1) {
                        index = index + 2 + 1;
                        let item = dataGrid.find((r) => r[0] === task.userTaskId.toString());
                        item[index] = this.trimTimestamp(timespan);
                    }
                }
            }
        }

        dataGrid = dataGrid.map((r) => {
            r.shift();
            return r;
        });

        dataGrid = dataGrid.map((r) => {
            let totalMinutes = r.reduce((a, v, i) => {
                if (i > 1 && i <= r.length - 3) {
                    a += this.timestampToMinutes(v);
                }
                return a;
            }, 0);
            r[r.length - 2] = this.trimTimestamp(this.minutesToTimestamp(totalMinutes));
            return r;
        });

        let totalOfTotalTimesMin = dataGrid.reduce((a, v) => {
            a += this.timestampToMinutes(v[v.length - 2]);
            return a;
        }, 0);
        dataGrid.push([...new Array(headerArray.length - 1 - 2).fill(""), "Total", this.minutesToTimestamp(totalOfTotalTimesMin), ""]);

        xlsx.utils.sheet_add_aoa(worksheet, dataGrid, {
            origin: "A7",
        });

        // styles
        worksheet["!cols"] = [
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
        ];
        worksheet["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 6 } }];

        for (let i in worksheet) {
            if (typeof worksheet[i] != "object") continue;
            let cell = xlsx.utils.decode_cell(i);
            worksheet[i].s = {
                font: {
                    name: "Calibri",
                    sz: 11,
                    color: { rgb: "000000" },
                },
                border: {
                    top: {
                        style: "thin",
                        color: { rgb: "000000" },
                    },
                    bottom: {
                        style: "thin",
                        color: { rgb: "000000" },
                    },
                    left: {
                        style: "thin",
                        color: { rgb: "000000" },
                    },
                    right: {
                        style: "thin",
                        color: { rgb: "000000" },
                    },
                },
            };

            if (cell.r == 5) {
                worksheet[i].s.font.bold = true;
                worksheet[i].s.fill = {
                    patternType: "solid",
                    fgColor: { rgb: "DDDDDD" },
                };
            }

            if (worksheet[i].v === "Total") {
                worksheet[i].s.font.bold = true;
            }

            if ((cell.r == 1 || cell.r == 2 || cell.r == 3) && cell.c == 0) {
                worksheet[i].s.font.bold = true;
                worksheet[i].s.fill = {
                    patternType: "solid",
                    fgColor: { rgb: "DDDDDD" },
                };
            }
            if (cell.r== 0) {
                worksheet[i].s.font.bold = true;
                worksheet[i].s.font.underline = true; 
                worksheet[i].s.font.sz = 12;
                worksheet[i].s.fill = {
                    patternType: "solid",
                    fgColor: { rgb: "FFFFFF" },
                };
                worksheet[i].s.alignment = {
                    vertical: "center",
                        horizontal: "center",
                        wrapText: '1',
                }
            }
            
        }

        xlsx.utils.book_append_sheet(workbook, worksheet, "Sheet 1");
        const excelBuffer: any = xlsx.write(workbook, {
            bookType: "xlsx",
            type: "array",
        });
        this.saveAsExcelFile(
            excelBuffer,
            `${_.capitalize(timesheet.projectName)} - ${_.capitalize(timesheet.userName)} - ${this.getTimesheetDateRangeOnlyDates(timesheet)}`
        );
    }

    getDateArrayFromDateRange(startDate: Date, endDate: Date): Date[] {
        let arr = [];
        let date = startDate;
        while (date.getTime() <= endDate.getTime()) {
            arr.push(date);
            date = moment(new Date(date.toString())).clone().add(1, "day").toDate();
        }
        return arr;
    }

    exportMyTimesheetsListToPdf(timesheets: TimeSheetDto[], columns: { title: string; dataKey: string }[]) {

        let exportColumns = columns;
        const doc = new jsPDF("l", "pt");
        doc.setFontSize(12);
        doc.text("My Timesheets Report", 40, 40);
        doc.autoTable(
            exportColumns,
            timesheets.map((r) => this.mapTimesheetToExportTimesheet(r)),
            {
                startY: 60,
            }
        );
        doc.save(`My Timesheets Report.pdf`);
    }

    exportTeamTimesheetsListToPdf(timesheets: TimeSheetDto[], columns: { title: string; dataKey: string }[]) {
        let exportColumns = columns;
        const doc = new jsPDF("l", "pt");
        doc.setFontSize(12);
        doc.text("Team Timesheets Report", 40, 40);
        doc.autoTable(
            exportColumns,
            timesheets.map((r) => this.mapTimesheetToExportTimesheet(r)),
            {
                startY: 60,
            }
        );
        doc.save(`Team Timesheets Report.pdf`);
    }

    exportMyTimesheetsListToExcel(timesheets: TimeSheetDto[]) {
        const workbook: xlsx.WorkBook = xlsx.utils.book_new();
        const worksheet: xlsx.WorkSheet = xlsx.utils.aoa_to_sheet([]);
        xlsx.utils.sheet_add_aoa(worksheet, [["My Timesheets Report"]], { origin: "A1" });
        xlsx.utils.sheet_add_aoa(
            worksheet,
            [
                [
                    "Submitted On",
                    "Project Name",
                    "Timesheet Date",
                    //"Submission Frequency",
                    "Required Hours",
                    "Submitted Hours",
                    "Billed Hours",
                    "Status",
                ],
            ],
            {
                origin: "A2",
            }
        );
        let data = timesheets.map((ts) => {
            let arr = [];

            arr.push(new Date(ts.submitedDate as any));

            arr.push(_.capitalize(ts.projectName));
            arr.push(this.getTimesheetFromToDateString(ts.fromDate, ts.toDate));
            arr.push(this.getFormattedHoursAbove24Hrs(ts.requiredHours));
            arr.push(this.getFormattedHoursAbove24Hrs(ts.submitedHours));
            arr.push(this.getFormattedHoursAbove24Hrs(ts.billedHours));
            arr.push(this.getTimesheetStatus(ts.timesheetStatus));
            return arr;
        });
        xlsx.utils.sheet_add_aoa(worksheet, data, { origin: "A3" });
           // Merge and center cells A1 to F1
worksheet["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }];

        worksheet["!cols"] = [
            { width: 20 },
            { width: 20 },
            { width: 25 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
        ];
        for (let i in worksheet) {
            if (typeof worksheet[i] != "object") continue;
            let cell = xlsx.utils.decode_cell(i);
            worksheet[i].s = {
                font: {
                    name: "Calibri",
                    sz: 11,
                    color: { rgb: "000000" },
                },
                border: {
                    top: {
                        style: "thin",
                        color: { rgb: "000000" },
                    },
                    bottom: {
                        style: "thin",
                        color: { rgb: "000000" },
                    },
                    left: {
                        style: "thin",
                        color: { rgb: "000000" },
                    },
                    right: {
                        style: "thin",
                        color: { rgb: "000000" },
                    },
                },
            };

            if (cell.r == 1) {
                worksheet[i].s.font.bold = true;
                worksheet[i].s.fill = {
                    patternType: "solid",
                    fgColor: { rgb: "DDDDDD" },
                };
            }
            if (cell.r == 0) {
                worksheet[i].s.font.bold = true;
                worksheet[i].s.font.underline = true; 
                worksheet[i].s.font.sz = 12;
                worksheet[i].s.fill = {
                    patternType: "solid",
                    fgColor: { rgb: "FFFFFF" },

                };
                worksheet[i].s.alignment = {
                    vertical: "center",
                        horizontal: "center",
                        wrapText: '1',
                }
            }
            if (cell.c == 4 || cell.c == 3) {
                worksheet[i].z = '0.00';
            }
            if (cell.c == 0 || cell.c == 2) {
                worksheet[i].z = 'dd-mmm-yyyy';
            }
        }
        xlsx.utils.book_append_sheet(workbook, worksheet, "Sheet 1");
        const excelBuffer: any = xlsx.write(workbook, {
            bookType: "xlsx",
            type: "array",
        });
        this.saveAsExcelFile(excelBuffer, "My Timesheets Report");
    }

    exportTeamTimesheetsListToExcel(timesheets: TimeSheetDto[]) {
        const workbook: xlsx.WorkBook = xlsx.utils.book_new();
        const worksheet: xlsx.WorkSheet = xlsx.utils.aoa_to_sheet([]);
        xlsx.utils.sheet_add_aoa(worksheet, [["Team Timesheets Report"]], { origin: "A1" });
        xlsx.utils.sheet_add_aoa(
            worksheet,
            [["Members", "Project Name", "Timesheet Date", "Submitted On","Required Hours ", "Submitted Hours", "Billed Hours", "Status"]],
            {
                origin: "A2",
            }
        );
        let data = timesheets.map((ts) => {
            let arr = [];

            arr.push(ts.userName);
            arr.push(ts.projectName);
            arr.push(this.getTimesheetFromToDateString(ts.fromDate, ts.toDate));
            arr.push(new Date(this.getLocalJsDate(ts.submitedDate)));

            arr.push(this.getFormattedHoursAbove24Hrs(ts.requiredHours));
            arr.push(this.getFormattedHoursAbove24Hrs(ts.submitedHours));
            arr.push(this.getFormattedHoursAbove24Hrs(ts.billedHours));
            arr.push(this.getTimesheetStatus(ts.timesheetStatus));
            return arr;
        });
        xlsx.utils.sheet_add_aoa(worksheet, data, { origin: "A3" });
           // Merge and center cells A1 to F1
worksheet["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }];
        worksheet["!cols"] = [
            { width: 20 },
            { width: 20 },
            { width: 30 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
        ];
        for (let i in worksheet) {
            if (typeof worksheet[i] != "object") continue;
            let cell = xlsx.utils.decode_cell(i);
            worksheet[i].s = {
                font: {
                    name: "Calibri",
                    sz: 11,
                    color: { rgb: "000000" },
                },
                border: {
                    top: {
                        style: "thin",
                        color: { rgb: "000000" },
                    },
                    bottom: {
                        style: "thin",
                        color: { rgb: "000000" },
                    },
                    left: {
                        style: "thin",
                        color: { rgb: "000000" },
                    },
                    right: {
                        style: "thin",
                        color: { rgb: "000000" },
                    },
                },
            };

            if (cell.r == 1) {
                worksheet[i].s.font.bold = true;
                worksheet[i].s.fill = {
                    patternType: "solid",
                    fgColor: { rgb: "DDDDDD" },
                };
            }
            if (cell.r == 0) {
                worksheet[i].s.font.bold = true;
                worksheet[i].s.font.underline = true; 
                worksheet[i].s.font.sz = 12;
                worksheet[i].s.fill = {
                    patternType: "solid",
                    fgColor: { rgb: "FFFFFF" },

                };
                worksheet[i].s.alignment = {
                    vertical: "center",
                        horizontal: "center",
                        wrapText: '1',
                }
            }
            if (cell.c == 5 || cell.c == 4) {
                worksheet[i].z = '0.00';
            }
            if (cell.c == 3) {
                worksheet[i].z = 'dd-mmm-yyyy';
            }
        }
        xlsx.utils.book_append_sheet(workbook, worksheet, "Sheet 1");
        const excelBuffer: any = xlsx.write(workbook, {
            bookType: "xlsx",
            type: "array",
        });
        this.saveAsExcelFile(excelBuffer, "Team Timesheets Report");
    }

    saveAsExcelFile(buffer: any, fileName: string): void {
        let EXCEL_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        let EXCEL_EXTENSION = ".xlsx";
        const data: Blob = new Blob([buffer], {
            type: EXCEL_TYPE,
        });
        saveAs(data, fileName +  EXCEL_EXTENSION);
    }

    // returns array which has the dates in the timesheet perios used to get tasks for a week which has that date
    getDatesArrayFromFrequency(timesheet: TimeSheetDto, timesheetFrequency: ProjectTimesheetFrequencyEnum) {
        let datesArray = [];
        if (timesheetFrequency === ProjectTimesheetFrequencyEnum.Daily || timesheetFrequency === ProjectTimesheetFrequencyEnum.Weekly) {
            datesArray.push(this.setZeroTimeForLocalDate(this.getLocalJsDate(timesheet.fromDate)));
        } else if (timesheetFrequency === ProjectTimesheetFrequencyEnum.BiMonthly) {
            let fromDate = this.setZeroTimeForLocalDate(this.getLocalJsDate(timesheet.fromDate));
            datesArray.push(fromDate);
            datesArray.push(moment(new Date(fromDate.toString())).clone().add(7, "days").toDate());
        } else if (timesheetFrequency === ProjectTimesheetFrequencyEnum.Monthly) {
            let fromDate = this.setZeroTimeForLocalDate(this.getLocalJsDate(timesheet.fromDate));
            let month = fromDate.getMonth();
            while (fromDate.getMonth() === month) {
                datesArray.push(fromDate);
                fromDate = moment(new Date(fromDate.toString())).clone().add(7, "days").toDate();
            }
        }
        return datesArray;
    }

    getDatesArrayFromFrequencyForReport(timesheet: EmployeeWiseSummaryDto, timesheetFrequency: ProjectTimesheetFrequencyEnum) {
        let datesArray = [];
        if (timesheetFrequency === ProjectTimesheetFrequencyEnum.Daily || timesheetFrequency === ProjectTimesheetFrequencyEnum.Weekly) {
            datesArray.push(this.setZeroTimeForLocalDate(this.getLocalJsDate(DateTime.fromISO(timesheet.timesheetDate))));
        } else if (timesheetFrequency === ProjectTimesheetFrequencyEnum.BiMonthly) {
            let fromDate = this.setZeroTimeForLocalDate(this.getLocalJsDate(DateTime.fromISO(timesheet.timesheetDate)));
            datesArray.push(fromDate);
            datesArray.push(moment(new Date(fromDate.toString())).clone().add(7, "days").toDate());
        } else if (timesheetFrequency === ProjectTimesheetFrequencyEnum.Monthly) {
            let fromDate = this.setZeroTimeForLocalDate(this.getLocalJsDate(DateTime.fromISO(timesheet.timesheetDate)));
            let month = fromDate.getMonth();
            while (fromDate.getMonth() === month) {
                datesArray.push(fromDate);
                fromDate = moment(new Date(fromDate.toString())).clone().add(7, "days").toDate();
            }
        }
        return datesArray;
    }

    showTooltipForString(str: string, maxLength: number) {
        if (str && str.length <= maxLength) return true;
        return false;
    }

    getWeekDay() {
        let toDay = new Date();
        let weeksday = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        return weeksday[toDay.getDay()];
    }
    getFormattedHoursAbove24Hrs(hourString: string): number {
        let days;
        const lastIndex = hourString.lastIndexOf('.');
        if (lastIndex > 6) { hourString = hourString.substring(0, lastIndex); }
        if (hourString.includes('.')) days = parseInt(hourString.split(".")[0]) * 24;
        if (days) hourString = hourString.split(".")[1];
        let timeSplit = hourString.split(':');
        var hrs = days ? parseInt(hourString.split(":")[0]) + days : parseInt(hourString.split(":")[0]);
        var mins = parseInt(timeSplit[1]);
        //code to convert hours to decimal
        var decimalHours = (hrs + (mins / 60));
        var formattedDecimalHours = decimalHours.toFixed(2)
        return Number(formattedDecimalHours);
    }
    getHoursAndMinStringWithColon(timespan: string): any {
        const lastIndex = timespan.lastIndexOf('.'); // "08:39:59.9999999" for this kind of string we are removing ".999999"
        if (lastIndex > 6) { timespan = timespan.substring(0, lastIndex); }
        let days;
        if (!timespan) return '00:00';
        if (timespan.includes('.')) days = parseInt(timespan.split(".")[0]) * 24;
        if (days) timespan = timespan.split(".")[1];
        let hours = days ? parseInt(timespan.split(":")[0]) + days : parseInt(timespan.split(":")[0]);
        let min = timespan.split(":")[1] ? parseInt(timespan.split(":")[1]) : 0;
        return `${hours < 10 ? "0" + hours : hours}:${min < 10 ? "0" + min : min} h`;
    }
    sortObjectBykeys(obj) {
        let entries = Object.entries(obj);

        entries.sort((a, b) => {
        if (a[0] < b[0]) {
            return -1;
        } else if (a[0] > b[0]) {
            return 1;
        } else {
            return 0;
        }
        });
        let sortedObj = {};
        entries.forEach(entry => {
            sortedObj[entry[0]] = entry[1];
        });
        return sortedObj;
    }
}
